<template>
  <div
    :id="post.id"
    :class="[
      'post-container',
      {
        'post-container-view-only': viewOnly,
      },
    ]"
  >
    <div class="post-header">
      <div class="owner-container">
        <Avatar
          :thumb-url="postOwner.thumbUrl"
          :name="postOwner.fullName"
          :deleted-at="postOwner.deletedAt"
        />
        <div class="owner-content">
          <div :class="['owner-details', { removed: !postOwner.id || postOwner.deletedAt }]">
            <FriedH4>{{ postOwner.fullName || t('dr_chat_removed_participant') }} </FriedH4>
            <FriedParagraph :size="ParagraphSize.Small" class="small-paragraph">{{
              postOwnerTitle
            }}</FriedParagraph>
          </div>
          <FriedParagraph :size="ParagraphSize.Small" class="small-paragraph">{{
            formattedDate(fromMillis(post.createdAt))
          }}</FriedParagraph>
        </div>
      </div>
      <PostOptionsButton
        v-if="!viewOnly && canEdit"
        :post="post"
        @select="$emit('select', $event)"
      />
    </div>
    <div v-if="text" class="post-text-container">
      <InlineRichTextEditorViewer :content="text" class="post-text" />
    </div>
    <slot name="media" />
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';
import { DateTime } from 'luxon';
import { ParagraphSize } from '@getaccept/fried-tofu';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import InlineRichTextEditorViewer from '../../inline-text-editor/components/InlineRichTextEditorViewer.vue';
import type { Post, Participant } from '../../types';
import PostHelper from '../helpers/post.helper';
import Avatar from '../../components/Avatar.vue';
import PostOptionsButton from './PostOptionsButton.vue';

export default defineComponent({
  components: {
    InlineRichTextEditorViewer,
    PostOptionsButton,
    Avatar,
  },
  props: {
    post: {
      type: Object as PropType<Post>,
      required: true,
    },
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    viewOnly: {
      type: Boolean,
    },
    canEdit: {
      type: Boolean,
    },
  },
  emits: ['select'],
  setup(props) {
    const postOwner = computed(
      () =>
        props.participants?.find(participant => participant.id === props.post.participantId) || {
          id: null,
          fullName: null,
          thumbUrl: null,
          title: null,
          deletedAt: null,
        }
    );

    const postOwnerTitle = computed(() =>
      postOwner.value?.title ? ` ${postOwner.value.title}` : ''
    );

    const text = computed(() =>
      PostHelper.isEmptyText(props.post.text) ? null : JSON.parse(props.post.text)
    );

    return {
      postOwner,
      postOwnerTitle,
      formattedDate: DateHelper.formattedDate,
      fromMillis: DateTime.fromMillis,
      ParagraphSize,
      text,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '../../scss/removed-participant';
@import '../../feed/scss/post-details';
</style>
